package uz.mint.frontend.components.sections

import androidx.compose.runtime.*
import com.varabyte.kobweb.compose.css.*
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.foundation.layout.Row
import com.varabyte.kobweb.compose.foundation.layout.Spacer
import com.varabyte.kobweb.compose.ui.Alignment
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.graphics.Colors
import com.varabyte.kobweb.compose.ui.modifiers.*
import com.varabyte.kobweb.compose.ui.toAttrs
import com.varabyte.kobweb.silk.components.navigation.Link
import com.varabyte.kobweb.silk.components.navigation.UncoloredLinkVariant
import com.varabyte.kobweb.silk.components.navigation.UndecoratedLinkVariant
import com.varabyte.kobweb.silk.components.text.SpanText
import com.varabyte.kobweb.silk.theme.colors.ColorMode
import kotlinx.browser.window
import kotlinx.coroutines.launch
import org.jetbrains.compose.web.attributes.InputType
import org.jetbrains.compose.web.css.*
import uz.mint.frontend.api.AccountApi
import uz.mint.frontend.api.AccountSession
import uz.mint.frontend.api.Customer
import uz.mint.frontend.api.CustomerOrder
import uz.mint.frontend.api.SessionStore
import uz.mint.frontend.api.formatUzs
import uz.mint.frontend.components.widgets.SecondaryButton
import uz.mint.frontend.flexWrapCss
import uz.mint.frontend.i18n.AccountStrings
import uz.mint.frontend.i18n.Locale
import uz.mint.frontend.i18n.accountStringsFor
import uz.mint.frontend.i18n.stringsFor
import uz.mint.frontend.toSitePalette

/**
 * Profile, order history and active sessions.
 *
 * All three are ours: eSIMAccess has no customer record to source any of it from. The phone is
 * shown but not editable — it is the account, and changing it would silently move every order
 * and eSIM to a different person.
 */
@Composable
fun ProfilePage(locale: Locale) {
    val account = accountStringsFor(locale)

    AccountShell(locale, account.profileTitle) { customer, strings ->
        Column(Modifier.fillMaxWidth().gap(20.px)) {
            ProfileForm(locale, customer, strings)
            OrdersCard(locale, strings)
            SessionsCard(strings)
            SignOutRow(locale, strings)
        }
    }
}

@Composable
private fun ProfileForm(locale: Locale, customer: Customer, account: AccountStrings) {
    val palette = ColorMode.current.toSitePalette()
    val scope = rememberCoroutineScope()

    var name by remember(customer.id) { mutableStateOf(customer.name.orEmpty()) }
    var chosenLocale by remember(customer.id) { mutableStateOf(customer.locale) }
    var busy by remember { mutableStateOf(false) }
    var notice by remember { mutableStateOf<String?>(null) }

    AccountCard(account.profileTitle) {
        Column(Modifier.fillMaxWidth().gap(6.px)) {
            SpanText(
                account.profilePhone,
                Modifier.fontSize(12.px).fontWeight(FontWeight.Bold).letterSpacing(0.08.em)
                    .textTransform(TextTransform.Uppercase).color(palette.ink2),
            )
            SpanText(customer.phoneFormatted, Modifier.fontSize(18.px).fontWeight(FontWeight.Bold))
            SpanText(account.profilePhoneNote, Modifier.fontSize(13.px).color(palette.ink2))
        }

        LabelledField(account.profileName) {
            TextField(
                value = name,
                placeholder = account.profileNamePlaceholder,
                type = InputType.Text,
                onValue = { name = it },
            )
        }

        LabelledField(account.profileLanguage) {
            Row(Modifier.gap(10.px).flexWrapCss()) {
                Locale.entries.forEach { entry ->
                    val selected = chosenLocale == entry.code
                    org.jetbrains.compose.web.dom.Button(
                        attrs = Modifier
                            .padding(topBottom = 11.px, leftRight = 18.px)
                            .border(2.px, LineStyle.Solid, palette.rule)
                            .backgroundColor(if (selected) palette.solidBg else Colors.Transparent)
                            .color(if (selected) palette.solidFg else palette.ink)
                            .fontSize(14.px)
                            .fontWeight(FontWeight.Bold)
                            .fontFamily("inherit")
                            .cursor(Cursor.Pointer)
                            .onClick { chosenLocale = entry.code }
                            .toAttrs()
                    ) { org.jetbrains.compose.web.dom.Text(entry.nativeName) }
                }
            }
        }

        Row(Modifier.gap(16.px).flexWrapCss(), verticalAlignment = Alignment.CenterVertically) {
            SecondaryButton(if (busy) account.profileSaving else account.profileSave) {
                if (!busy) {
                    busy = true
                    notice = null
                    scope.launch {
                        runCatching { AccountApi.updateProfile(name, chosenLocale) }
                            .onSuccess { updated ->
                                updated?.let(SessionStore::updated)
                                notice = account.profileSaved
                                // The site language is a route, not a preference, so a changed
                                // language only takes visible effect by going to that route.
                                if (updated != null && updated.locale != locale.code) {
                                    Locale.entries.firstOrNull { it.code == updated.locale }
                                        ?.let { window.location.assign(it.profilePath) }
                                }
                            }
                            .onFailure { notice = account.profileSaveError }
                        busy = false
                    }
                }
            }
            notice?.let {
                SpanText(it, Modifier.fontSize(14.px).fontWeight(FontWeight.SemiBold).color(palette.ink2))
            }
        }
    }
}

@Composable
private fun OrdersCard(locale: Locale, account: AccountStrings) {
    val palette = ColorMode.current.toSitePalette()
    val strings = stringsFor(locale)
    var orders by remember { mutableStateOf<List<CustomerOrder>?>(null) }

    LaunchedEffect(Unit) {
        runCatching { AccountApi.orders() }.onSuccess { orders = it }.onFailure { orders = emptyList() }
    }

    AccountCard(account.ordersTitle) {
        val list = orders
        when {
            list == null -> SpanText(account.esimsLoading, Modifier.fontSize(14.px).color(palette.ink2))
            list.isEmpty() -> SpanText(account.ordersEmpty, Modifier.fontSize(14.px).color(palette.ink2))
            else -> Column(Modifier.fillMaxWidth().gap(12.px)) {
                list.forEach { order ->
                    // A real link, not a click handler: the detail page has an address, so a
                    // customer can bookmark a purchase or open it in a new tab — and the row
                    // announces itself as a link to anyone using a screen reader.
                    Link(
                        path = locale.orderPath(order.reference),
                        modifier = Modifier.fillMaxWidth(),
                        variant = UndecoratedLinkVariant.then(UncoloredLinkVariant),
                    ) {
                        Row(
                            Modifier.fillMaxWidth().gap(14.px).flexWrapCss()
                                .padding(bottom = 12.px)
                                .borderBottom(2.px, LineStyle.Solid, palette.rule),
                            verticalAlignment = Alignment.CenterVertically,
                        ) {
                            Column(Modifier.gap(3.px)) {
                                SpanText(order.planName, Modifier.fontSize(15.px).fontWeight(FontWeight.Bold))
                                SpanText(
                                    "${order.reference} · ${formatDate(order.createdAt)} · " +
                                        account.orderStatus(order.status),
                                    Modifier.fontSize(13.px).color(palette.ink2),
                                )
                            }
                            Spacer()
                            SpanText(
                                order.priceUzs?.let { formatUzs(it, strings) } ?: "—",
                                Modifier.fontSize(15.px).fontWeight(FontWeight.Bold).whiteSpace(WhiteSpace.NoWrap),
                            )
                            SpanText(
                                "›",
                                Modifier.fontSize(20.px).fontWeight(FontWeight.Bold)
                                    .color(palette.accentText).lineHeight(1),
                            )
                        }
                    }
                }
            }
        }
    }
}

@Composable
private fun SessionsCard(account: AccountStrings) {
    val palette = ColorMode.current.toSitePalette()
    val scope = rememberCoroutineScope()
    var sessions by remember { mutableStateOf<List<AccountSession>?>(null) }
    var attempt by remember { mutableStateOf(0) }
    var notice by remember { mutableStateOf<String?>(null) }

    LaunchedEffect(attempt) {
        runCatching { AccountApi.sessions() }.onSuccess { sessions = it }.onFailure { sessions = emptyList() }
    }

    AccountCard(account.sessionsTitle, account.sessionsLede) {
        val list = sessions
        when {
            list == null -> SpanText(account.esimsLoading, Modifier.fontSize(14.px).color(palette.ink2))
            list.isEmpty() -> SpanText(account.sessionsEmpty, Modifier.fontSize(14.px).color(palette.ink2))
            else -> Column(Modifier.fillMaxWidth().gap(10.px)) {
                list.forEach { session ->
                    Row(
                        Modifier.fillMaxWidth().gap(12.px).flexWrapCss(),
                        verticalAlignment = Alignment.CenterVertically,
                    ) {
                        Column(Modifier.gap(2.px)) {
                            SpanText(
                                session.userAgent?.take(60) ?: "—",
                                Modifier.fontSize(14.px).fontWeight(FontWeight.SemiBold),
                            )
                            SpanText(
                                listOfNotNull(
                                    formatDate(session.lastSeenAt).takeIf { it.isNotBlank() },
                                    session.ipAddress,
                                    account.sessionsCurrent.takeIf { session.current },
                                ).joinToString(" · "),
                                Modifier.fontSize(13.px).color(palette.ink2),
                            )
                        }
                    }
                }

                // Only offered when there is something else to sign out of.
                if (list.size > 1) {
                    SecondaryButton(account.sessionsRevokeOthers) {
                        scope.launch {
                            runCatching { AccountApi.revokeOtherSessions() }
                                .onSuccess {
                                    notice = account.sessionsRevoked
                                    attempt++
                                }
                        }
                    }
                }
                notice?.let { SpanText(it, Modifier.fontSize(14.px).color(palette.ink2)) }
            }
        }
    }
}

@Composable
private fun SignOutRow(locale: Locale, account: AccountStrings) {
    val scope = rememberCoroutineScope()

    Row(Modifier.fillMaxWidth().gap(16.px).flexWrapCss()) {
        SecondaryButton(account.navLogout) {
            scope.launch {
                SessionStore.signOut()
                window.location.assign(locale.homePath)
            }
        }
    }
}

@Composable
private fun LabelledField(label: String, field: @Composable () -> Unit) {
    val palette = ColorMode.current.toSitePalette()
    Column(Modifier.fillMaxWidth().gap(7.px)) {
        SpanText(
            label,
            Modifier.fontSize(12.px).fontWeight(FontWeight.Bold).letterSpacing(0.08.em)
                .textTransform(TextTransform.Uppercase).color(palette.ink2),
        )
        field()
    }
}
